/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ReferenceTradingDataRequestDetail } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/request/GetReferenceTradingDataRequest';
import { ReferenceField } from '@1po/1po-bff-fe-spec/generated/estimate/request/UpdateReference';
import { Reference } from '@1po/1po-bff-fe-spec/generated/estimate/response/model/Reference';
import { Table } from 'antd';
import { RootState } from 'app/AppStore';
import { ChevronDownIcon, ChevronUpIcon, UnlinkIcon } from 'assets/icons';
import { useIsB2B } from 'components/B2BComponents/useIsB2B';
import { Dialog, useDisclosure } from 'components/Dialog';
import { DiscountCorner } from 'components/Discount';
import {
  getCatalogReferences,
  getOperationReferences,
  getPendingReferenceValidations,
  getReferences,
  removeKnownReferenceValidation,
  removeOperationReference,
  removeReference,
  updateOperationReference,
  updateReference,
} from 'domains/estimate/Estimate.store';

import { DHReferenceLocal } from 'domains/references';
import { useFetchDiscounts } from 'domains/references/References.requests';
import { ClientView, getCurrency } from 'domains/user';
import { EstimateGarageMargin } from 'pages/EstimatePage/TableSection/EstimateGarageMargin';
import { EstimateGaragePrice } from 'pages/EstimatePage/TableSection/EstimateGaragePrice';
import { EstimateMarginAlert } from 'pages/EstimatePage/TableSection/EstimateMarginAlert';
import { IncompatibleReferenceIndicator } from 'pages/EstimatePage/TableSection/IncompatibleReferenceIndicator';
import { LinkedReferenceTable } from 'pages/EstimatePage/TableSection/LinkedReferenceTable';
import ReferenceAutocomplete from 'pages/EstimatePage/TableSection/ReferenceAutocomplete';
import {
  B2bStockDisplay,
  DiscountedPrice,
  ProductPriceB2b,
} from 'pages/EstimatePage/TableSection/ReferenceTable/common';
import {
  DiscountInput,
  EstimatePriceInput,
  GetTitle,
  QuantityInput,
  TextInput,
  VatInput,
} from 'pages/EstimatePage/TableSection/tableComponents';
import { clientViewMarginValidator, TableProps } from 'pages/EstimatePage/TableSection/TableSection';
import { STable } from 'pages/EstimatePage/TableSection/TableSection.styled';
import { Flex, Icon, Text } from 'UI';
import { getCondArrayItem, NO_DATA } from 'utils';
import { textFormatter } from 'utils/format';
import { getMaxOrderQuantityForFamily, isSingleUnitOrderRestrictedFamily } from 'utils/rights';

export default function ReferenceTable({
  estimateId,
  sparePartsView,
  'data-cy': dataCy,
  operationId,
}: Readonly<TableProps>) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currency = useSelector(getCurrency);
  const data = useSelector((state: RootState) =>
    operationId ? getOperationReferences(state, { estimateId, operationId }) : getReferences(state, estimateId),
  );
  const catalogReferences = useSelector((state: RootState) => getCatalogReferences(state, estimateId));
  const validationRefs = useSelector(getPendingReferenceValidations);
  const disclosure = useDisclosure();
  const isB2B = useIsB2B();
  const isClientView = sparePartsView === ClientView;
  const { onOpen } = disclosure;
  const [validationRow, setValidationRow] = useState<{ itemId: string; referenceNumber: string } | undefined>(
    undefined,
  );
  const [checkedReferences, setCheckedReferences] = useState(new Map<string, DHReferenceLocal | NO_DATA>());

  const unitPriceLabel = GetTitle(t('common.price.unit_price', 'Unit Price'));

  const removeCheckedReferences = (k: string) => {
    checkedReferences.delete(k);
    setCheckedReferences(new Map(checkedReferences));
  };

  const tradingData: ReferenceTradingDataRequestDetail[] = data.map((ref) => {
    return {
      referenceNumber: ref.referenceNumber,
      commercialFamily: ref.commercialFamily ?? '',
    };
  });

  useFetchDiscounts(tradingData);

  useEffect(() => {
    if (operationId) {
      return;
    }
    validationRefs
      .flatMap((refForValidation) =>
        catalogReferences.filter((refData) => refData.referenceNumber === refForValidation),
      )
      .forEach((x) => {
        if (isB2B) {
          return;
        }
        if (!x.isCompatible) {
          setValidationRow({ itemId: x.itemId, referenceNumber: x.referenceNumber });
          onOpen();
        }
        dispatch(removeKnownReferenceValidation(x.referenceNumber));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationRefs, dispatch, catalogReferences, onOpen]);

  const handleUpdate = (itemId: string, field: ReferenceField) => (newValue: string) => {
    if (operationId) {
      dispatch(updateOperationReference({ itemId, operationId, field, newValue }));
      return;
    }
    dispatch(updateReference({ itemId, field, newValue }));
  };

  function continueAdding() {
    setValidationRow(undefined);
  }

  function removeRow(rowId: string) {
    const removedRef = catalogReferences.filter((e) => e.itemId === rowId);
    if (removedRef && removedRef.length === 1 && removedRef[0].referenceNumber) {
      removeCheckedReferences(removedRef[0].referenceNumber);
    }
    if (operationId) {
      dispatch(removeOperationReference({ itemId: rowId, operationId }));
      return;
    }
    dispatch(removeReference({ itemId: rowId }));
  }

  function revertRow() {
    if (validationRow) {
      const refItemToRevert = catalogReferences.filter(
        (refData) => refData.referenceNumber === validationRow.referenceNumber,
      );
      if (refItemToRevert.length === 1) {
        const referenceToRevert = refItemToRevert[0];
        const originalQuantity = Number(referenceToRevert.quantity) >= 1 ? Number(referenceToRevert.quantity) - 1 : 0;
        if (originalQuantity === 0) {
          dispatch(removeReference({ itemId: validationRow.itemId }));
        } else {
          dispatch(
            updateReference({ itemId: validationRow.itemId, field: 'QUANTITY', newValue: String(originalQuantity) }),
          );
        }
      }
      dispatch(removeKnownReferenceValidation(validationRow.referenceNumber));
      setValidationRow(undefined);
    }
  }

  const tableColumnDiscountCorner = {
    title: '',
    dataIndex: 'referenceNumber',
    render: function rowSelector(referenceNumber: string) {
      return <DiscountCorner reference={referenceNumber} type={'estimate'} />;
    },
  };

  const tableColumnReferenceNumber = {
    title: GetTitle(t('catalog.reference', 'Reference')),
    dataIndex: 'referenceNumber',
    width: '8vw',
    render: function rowSelector(referenceNumber: string, row: Reference) {
      const input = operationId ? (
        <TextInput
          value={referenceNumber}
          saveFunction={handleUpdate(row.itemId, 'REFERENCE_NUMBER')}
          removeRow={() => removeRow(row.itemId)}
          readOnly={!row.isCustom}
          minWidth={140}
          isInvalid={clientViewMarginValidator({ isClientView, row })}
          data-cy={'reference-number'}
        />
      ) : (
        <ReferenceAutocomplete
          value={referenceNumber ?? ''}
          saveFunction={handleUpdate(row.itemId, 'REFERENCE_NUMBER')}
          rowId={row.itemId}
          fieldId={'REFERENCE_NUMBER'}
          removeRow={() => removeRow(row.itemId)}
          readOnly={!row.isCustom}
          minWidth={150}
          isInvalid={clientViewMarginValidator({ isClientView, row })}
          data-cy={'reference-number'}
        />
      );
      return (
        <Flex justify={'center'} align={'center'} gap={5}>
          {input}
          <IncompatibleReferenceIndicator isCustom={row.isCustom} isCompatible={row.isCompatible} />
        </Flex>
      );
    },
  };

  const tableColumnDesignation = {
    title: GetTitle(t('common.designation', 'Designation')),
    dataIndex: 'designation',
    render: function rowSelector(designation: string, row: Reference) {
      const labelWithMKTPSellerName = row.sellerName ? `${designation} [${row.sellerName}]` : designation;
      if (operationId) {
        return (
          <TextInput
            value={labelWithMKTPSellerName}
            saveFunction={handleUpdate(row.itemId, 'NAME')}
            readOnly={!row.isCustom}
            isInvalid={clientViewMarginValidator({ isClientView, row })}
            data-cy={'reference-designation'}
          />
        );
      }
      return (
        <ReferenceAutocomplete
          value={labelWithMKTPSellerName ?? ''}
          saveFunction={handleUpdate(row.itemId, 'NAME')}
          rowId={row.itemId}
          fieldId={'NAME'}
          readOnly={!row.isCustom}
          isInvalid={clientViewMarginValidator({ isClientView, row })}
          data-cy={'reference-designation'}
        />
      );
    },
  };

  const tableColumnUnitPriceB2C = {
    title: unitPriceLabel,
    dataIndex: 'unitPrice',
    width: '10vw',
    render: function rowSelector(unitPrice: string, row: Reference) {
      return (
        <EstimatePriceInput
          value={unitPrice}
          saveFunction={handleUpdate(row.itemId, 'UNIT_PRICE')}
          readOnly={!row.isCustom}
          minWidth={125}
          isInvalid={clientViewMarginValidator({ isClientView, row })}
        />
      );
    },
  };

  const tableColumnUnitPriceB2B = {
    title: unitPriceLabel,
    dataIndex: 'referenceNumber',
    width: '10vw',
    render: function rowSelector(referenceNumber: string) {
      return (
        <DiscountedPrice
          referenceNumber={referenceNumber}
          estimateId={estimateId}
          currency={currency}
          data-cy={'unit-price'}
        />
      );
    },
  };

  const tableColumnQuantity = {
    title: GetTitle(t('common.quantity', 'Quantity')),
    dataIndex: 'quantity',
    width: '5vw',
    render: function rowSelector(_quantity: string, row: Reference) {
      return (
        <QuantityInput
          value={Number(row.quantity)}
          saveFunction={handleUpdate(row.itemId, 'QUANTITY')}
          removeRow={() => removeRow(row.itemId)}
          isInvalid={clientViewMarginValidator({ isClientView, row })}
          maxValue={getMaxOrderQuantityForFamily(row.commercialFamily)}
          isSingleUnitRestricted={isSingleUnitOrderRestrictedFamily(row.commercialFamily)}
          precisionMin={operationId ? undefined : 3}
          precisionMax={operationId ? undefined : 3}
          design={'v2'}
        />
      );
    },
  };

  const tableColumnDiscount = {
    title: GetTitle(t('common.discount', 'Discount')),
    dataIndex: 'discount',
    width: '10vw',
    render: function rowSelector(discount: string, row: Reference) {
      return (
        <DiscountInput
          value={discount}
          saveFunction={handleUpdate(row.itemId, 'DISCOUNT')}
          discountType={row.discountType}
          saveDiscountType={handleUpdate(row.itemId, 'DISCOUNT_TYPE')}
          isInvalid={clientViewMarginValidator({ isClientView, row })}
        />
      );
    },
  };

  const tableColumnVatExclPrice = {
    title: GetTitle(t('common.price.vat_excl_price', 'VAT excl.Price')),
    dataIndex: 'priceVatExcluded',
    width: '8vw',
    render: function rowSelector(priceVatExcluded: string, row: Reference) {
      return (
        <Flex justify={'center'} align={'center'}>
          <Text type={clientViewMarginValidator({ isClientView, row }) ? 'light_14_bold_red' : 'light_14'}>
            {textFormatter.formatCurrency(isNaN(Number(priceVatExcluded)) ? 0 : priceVatExcluded, currency)}
          </Text>
        </Flex>
      );
    },
  };

  const tableColumnVat = {
    title: GetTitle(t('common.price.vat.title', 'VAT')),
    dataIndex: 'vatPercentage',
    width: '5vw',
    render: function rowSelector(vat: number, row: Reference) {
      return (
        <VatInput
          value={vat}
          saveFunction={handleUpdate(row.itemId, 'VAT_PERCENTAGE')}
          readOnly={!row.isCustom}
          isInvalid={clientViewMarginValidator({ isClientView, row })}
        />
      );
    },
  };

  const tableColumnMarginAlert = {
    title: null,
    dataIndex: '',
    width: 20,
    render: function rowSelector(row: Reference) {
      return clientViewMarginValidator({ row }) && <EstimateMarginAlert t={t} />;
    },
  };

  const tableColumnGaragePrice = {
    title: GetTitle(t('common.price.garage_price', 'Garage Price')),
    dataIndex: 'garagePrice',
    width: '6vw',
    render: function rowSelector(_garagePrice: string, row: Reference) {
      return (
        <EstimateGaragePrice
          row={row}
          currency={currency}
          handleUpdate={handleUpdate}
          isInvalid={Number(row.margin) <= 0}
        />
      );
    },
  };

  const tableColumnMargin = {
    title: GetTitle(t('common.price.margin', 'Margin')),
    dataIndex: 'margin',
    width: '6vw',
    render: function rowSelector(_margin: string, row: Reference) {
      return (
        <Flex justify={'space-between'} align={'center'} minWidth={70}>
          {clientViewMarginValidator({ row }) ? <EstimateMarginAlert t={t} /> : <Flex minWidth={20} />}
          <EstimateGarageMargin
            estimateId={estimateId}
            row={row}
            currency={currency}
            isInvalid={Number(row.margin) <= 0}
          />
        </Flex>
      );
    },
  };

  const tableColumnStocks = {
    title: GetTitle(t('cart.reference_table.stock', 'Stock')),
    dataIndex: 'stock',
    render: function rowSelector(_stock: number, row: Reference) {
      return (
        <B2bStockDisplay
          referenceNumber={row.referenceNumber}
          referenceName={row.designation}
          estimateId={estimateId}
          quantity={Number(row.quantity)}
          familyCode={row.commercialFamily}
          sellerNumber={row.sellerNumber}
          data-cy={'stocks-display'}
        />
      );
    },
  };

  const tableColumnPcl = {
    title: GetTitle(t('common.price.pcl', 'PCL')),
    dataIndex: 'pcl',
    render: function rowSelector(_pcl: string, row: Reference) {
      return (
        <ProductPriceB2b
          referenceNumber={row.referenceNumber}
          currency={currency}
          estimateId={estimateId}
          data-cy={'product-price'}
        />
      );
    },
  };

  const tableColumnTotalPriceB2B = {
    title: GetTitle(t('common.price.total_price', 'Total price')),
    dataIndex: 'totalPrice',
    render: function rowSelector(_totalPrice: string, row: Reference) {
      return (
        <DiscountedPrice
          referenceNumber={row.referenceNumber}
          quantity={Number(row.quantity)}
          estimateId={estimateId}
          currency={currency}
          data-cy={'total-price'}
        />
      );
    },
  };

  const getColumns = () => {
    if (isB2B) {
      return [
        tableColumnDiscountCorner,
        tableColumnReferenceNumber,
        tableColumnDesignation,
        tableColumnStocks,
        tableColumnPcl,
        tableColumnUnitPriceB2B,
        tableColumnQuantity,
        tableColumnTotalPriceB2B,
        tableColumnMargin,
      ];
    }
    return [
      tableColumnDiscountCorner,
      tableColumnReferenceNumber,
      tableColumnDesignation,
      tableColumnUnitPriceB2C,
      tableColumnQuantity,
      tableColumnDiscount,
      tableColumnVatExclPrice,
      tableColumnVat,
      ...getCondArrayItem(isClientView && tableColumnMarginAlert),
      ...getCondArrayItem(!isClientView && tableColumnGaragePrice),
      ...getCondArrayItem(!isClientView && tableColumnMargin),
    ];
  };

  if (data.length === 0) {
    return null;
  }
  return (
    <>
      <STable<Reference>
        columns={[...getColumns(), Table.EXPAND_COLUMN]}
        dataSource={data}
        pagination={false}
        rowKey={(row) => row.itemId}
        size={'large'}
        expandable={{
          expandedRowRender: (record) => LinkedReferenceTable(estimateId, record, currency, sparePartsView) ?? null,
          expandIcon: ExpandIcon,
          rowExpandable: (record) => (record.linkedReferences && record.linkedReferences.length > 0) ?? false,
          defaultExpandAllRows: true,
        }}
        onRow={(record) =>
          ({ 'data-cy': `reference-row-${record.referenceNumber}` } as React.HTMLAttributes<HTMLElement>)
        }
        data-cy={dataCy}
      />

      <Dialog
        disclosure={disclosure}
        title={t('common.dialog.actionRequired', 'Action required')}
        description={[
          t(
            'estimate.noncompatible.confirmationQuestion',
            // eslint-disable-next-line max-len
            'The reference that you are trying to add to your estimate is not compatible with the identified vehicle. Do you want to add it anyway?',
          ),
        ]}
        icon={UnlinkIcon}
        status={'info'}
        handleConfirm={continueAdding}
        handleCancel={revertRow}
      />
    </>
  );
}

function ExpandIcon({ expanded, record, onExpand }: { expanded: boolean; record: Reference; onExpand: any }) {
  if (!record.linkedReferences || record.linkedReferences.length === 0) {
    return null;
  }

  return (
    <Icon
      IconComponent={expanded ? ChevronDownIcon : ChevronUpIcon}
      onClick={(e) => onExpand(record, e)}
      height={15}
      width={15}
    />
  );
}
